import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Flex,
  Text,
  Button,
  Input,
  IconButton,
  Badge,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
} from '@chakra-ui/react';
import {
  CaretDown,
  Plus,
  PencilSimple,
  Trash,
  MusicNotes,
  Buildings,
  Truck,
  Scales,
  Camera,
  MicrophoneStage,
  Lightning,
  Megaphone,
  PaintBrush,
  Wrench,
  Shield,
  Star,
  Briefcase,
  Ticket,
  Monitor,
  Package,
  Handshake,
  FirstAidKit,
  FilmSlate,
  Confetti,
  Gift,
  IconProps,
} from 'phosphor-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  useEventos,
  IEventoCategoria,
  IEventoServico,
  IChecklistTemplate,
  IChecklistTemplateGrupo,
} from '../../../hooks/useEventos';
import { IDataNoPagination } from '../../../types/shared';
import DialogModal from '../../../components/DialogModal';

interface EventoConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const COLOR_PRESETS = [
  '#EF4444',
  '#F97316',
  '#EAB308',
  '#22C55E',
  '#06B6D4',
  '#3B82F6',
  '#6366F1',
  '#A855F7',
  '#EC4899',
  '#78716C',
];

const ICON_OPTIONS: { key: string; Icon: React.FC<IconProps>; label: string }[] = [
  { key: 'MusicNotes', Icon: MusicNotes, label: 'Musica' },
  { key: 'MicrophoneStage', Icon: MicrophoneStage, label: 'Microfone' },
  { key: 'Camera', Icon: Camera, label: 'Camera' },
  { key: 'FilmSlate', Icon: FilmSlate, label: 'Filme' },
  { key: 'Monitor', Icon: Monitor, label: 'Monitor' },
  { key: 'Lightning', Icon: Lightning, label: 'Energia' },
  { key: 'Buildings', Icon: Buildings, label: 'Estrutura' },
  { key: 'Truck', Icon: Truck, label: 'Logistica' },
  { key: 'Package', Icon: Package, label: 'Pacote' },
  { key: 'Wrench', Icon: Wrench, label: 'Ferramentas' },
  { key: 'PaintBrush', Icon: PaintBrush, label: 'Arte' },
  { key: 'Scales', Icon: Scales, label: 'Juridico' },
  { key: 'Shield', Icon: Shield, label: 'Seguranca' },
  { key: 'FirstAidKit', Icon: FirstAidKit, label: 'Saude' },
  { key: 'Megaphone', Icon: Megaphone, label: 'Marketing' },
  { key: 'Briefcase', Icon: Briefcase, label: 'Negocios' },
  { key: 'Handshake', Icon: Handshake, label: 'Parceria' },
  { key: 'Ticket', Icon: Ticket, label: 'Ingresso' },
  { key: 'Star', Icon: Star, label: 'Destaque' },
  { key: 'Confetti', Icon: Confetti, label: 'Festa' },
  { key: 'Gift', Icon: Gift, label: 'Presente' },
];

const ICON_MAP: Record<string, React.FC<IconProps>> = Object.fromEntries(
  ICON_OPTIONS.map(o => [o.key, o.Icon]),
);

export default function EventoConfigModal({ isOpen, onClose }: EventoConfigModalProps) {
  const {
    getCategorias,
    createCategoria,
    updateCategoria,
    deleteCategoria,
    getServicos,
    createServico,
    updateServico,
    deleteServico,
    getChecklistTemplateGrupos,
    createChecklistTemplateGrupo,
    updateChecklistTemplateGrupo,
    deleteChecklistTemplateGrupo,
    createChecklistTemplate,
    updateChecklistTemplate,
    deleteChecklistTemplate,
  } = useEventos();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" mx={4} maxH="85vh">
        <ModalHeader fontSize="md" fontWeight="700" color="gray.800" pb={0}>
          Configuracoes de Eventos
          <Text fontSize="12px" color="gray.400" fontWeight="400" mt={0.5}>
            Gerencie categorias, servicos e checklists
          </Text>
        </ModalHeader>
        <ModalCloseButton borderRadius="full" />

        <ModalBody pb={6} pt={4} overflowY="auto">
          <Tabs variant="unstyled">
            <TabList gap={1} mb={4}>
              <Tab
                fontSize="13px"
                fontWeight="500"
                px={4}
                py={1.5}
                borderRadius="full"
                color="gray.500"
                _selected={{ bg: 'gray.900', color: 'white' }}
                transition="all 0.15s ease"
              >
                Categorias
              </Tab>
              <Tab
                fontSize="13px"
                fontWeight="500"
                px={4}
                py={1.5}
                borderRadius="full"
                color="gray.500"
                _selected={{ bg: 'gray.900', color: 'white' }}
                transition="all 0.15s ease"
              >
                Servicos
              </Tab>
              <Tab
                fontSize="13px"
                fontWeight="500"
                px={4}
                py={1.5}
                borderRadius="full"
                color="gray.500"
                _selected={{ bg: 'gray.900', color: 'white' }}
                transition="all 0.15s ease"
              >
                Checklists
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel p={0}>
                <CategoriasPanel
                  getCategorias={getCategorias}
                  createCategoria={createCategoria}
                  updateCategoria={updateCategoria}
                  deleteCategoria={deleteCategoria}
                  isOpen={isOpen}
                />
              </TabPanel>
              <TabPanel p={0}>
                <ServicosPanel
                  getCategorias={getCategorias}
                  getServicos={getServicos}
                  createServico={createServico}
                  updateServico={updateServico}
                  deleteServico={deleteServico}
                  isOpen={isOpen}
                />
              </TabPanel>
              <TabPanel p={0}>
                <ChecklistTemplatesPanel
                  getChecklistTemplateGrupos={getChecklistTemplateGrupos}
                  createChecklistTemplateGrupo={createChecklistTemplateGrupo}
                  updateChecklistTemplateGrupo={updateChecklistTemplateGrupo}
                  deleteChecklistTemplateGrupo={deleteChecklistTemplateGrupo}
                  createChecklistTemplate={createChecklistTemplate}
                  updateChecklistTemplate={updateChecklistTemplate}
                  deleteChecklistTemplate={deleteChecklistTemplate}
                  isOpen={isOpen}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

// ── Categorias Panel ──

function CategoriasPanel({
  getCategorias,
  createCategoria,
  updateCategoria,
  deleteCategoria,
  isOpen,
}: any) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formNome, setFormNome] = useState('');
  const [formCor, setFormCor] = useState('#3B82F6');
  const [formIcone, setFormIcone] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const { data: categoriasData } = useQuery<IDataNoPagination<IEventoCategoria[]>>({
    queryKey: ['evento-categorias'],
    queryFn: getCategorias,
    enabled: isOpen,
  });

  const categorias = categoriasData?.data ?? [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['evento-categorias'] });
    queryClient.invalidateQueries({ queryKey: ['evento-servicos'] });
  };

  const createMut = useMutation({
    mutationFn: (data: any) => createCategoria(data),
    onSuccess: () => {
      invalidate();
      toast.success('Categoria criada!');
      resetForm();
    },
    onError: () => toast.error('Erro ao criar categoria'),
  });

  const updateMut = useMutation({
    mutationFn: (data: any) => updateCategoria(data),
    onSuccess: () => {
      invalidate();
      toast.success('Categoria atualizada!');
      resetForm();
    },
    onError: () => toast.error('Erro ao atualizar'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteCategoria(id),
    onSuccess: () => {
      invalidate();
      toast.success('Categoria removida!');
    },
    onError: () => toast.error('Erro ao remover'),
  });

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormNome('');
    setFormCor('#3B82F6');
    setFormIcone('');
  };

  const handleEdit = (cat: IEventoCategoria) => {
    setEditingId(cat.id);
    setFormNome(cat.nome);
    setFormCor(cat.cor || '#3B82F6');
    setFormIcone(cat.icone || '');
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!formNome.trim()) {
      toast.error('Nome obrigatorio');
      return;
    }
    if (editingId) {
      updateMut.mutate({
        id: editingId,
        data: { nome: formNome.trim(), cor: formCor, icone: formIcone || undefined },
      });
    } else {
      createMut.mutate({
        nome: formNome.trim(),
        cor: formCor,
        icone: formIcone || undefined,
        ordem: categorias.length,
      });
    }
  };

  const confirmDelete = () => {
    if (deleteId) deleteMut.mutate(deleteId);
    setDeleteId(null);
    onDeleteClose();
  };

  return (
    <>
      <Box>
        {categorias.length === 0 && !showForm && (
          <Flex direction="column" align="center" py={8}>
            <Text fontSize="sm" color="gray.400">
              Nenhuma categoria criada
            </Text>
          </Flex>
        )}

        {categorias.map(cat => (
          <Flex
            key={cat.id}
            align="center"
            gap={3}
            py={2.5}
            px={3}
            borderRadius="lg"
            _hover={{ bg: 'gray.50' }}
            transition="background 0.1s"
          >
            <Flex
              w="28px"
              h="28px"
              borderRadius="8px"
              bg={cat.cor ? `${cat.cor}18` : 'gray.100'}
              align="center"
              justify="center"
              flexShrink={0}
            >
              {cat.icone && ICON_MAP[cat.icone] ? (
                React.createElement(ICON_MAP[cat.icone], {
                  size: 14,
                  color: cat.cor || '#718096',
                  weight: 'bold',
                })
              ) : (
                <Box w="10px" h="10px" borderRadius="full" bg={cat.cor || 'gray.300'} />
              )}
            </Flex>
            <Text fontSize="13px" fontWeight="500" color="gray.700" flex={1}>
              {cat.nome}
            </Text>
            <IconButton
              aria-label="Editar"
              icon={<PencilSimple size={14} />}
              size="xs"
              variant="ghost"
              color="gray.400"
              _hover={{ color: 'blue.500', bg: 'blue.50' }}
              borderRadius="md"
              onClick={() => handleEdit(cat)}
            />
            <IconButton
              aria-label="Excluir"
              icon={<Trash size={14} />}
              size="xs"
              variant="ghost"
              color="gray.400"
              _hover={{ color: 'red.500', bg: 'red.50' }}
              borderRadius="md"
              onClick={() => {
                setDeleteId(cat.id);
                onDeleteOpen();
              }}
            />
          </Flex>
        ))}

        {/* Form */}
        {showForm && (
          <Box
            mt={3}
            p={3}
            bg={editingId ? 'blue.50' : 'green.50'}
            borderRadius="lg"
            border="1px dashed"
            borderColor={editingId ? 'blue.200' : 'green.200'}
          >
            <Flex gap={2} align="end">
              <Box flex={1}>
                <Text fontSize="11px" fontWeight="600" color="gray.500" mb={1}>
                  Nome
                </Text>
                <Input
                  size="sm"
                  borderRadius="md"
                  value={formNome}
                  onChange={e => setFormNome(e.target.value)}
                  placeholder="Ex: Audiovisual"
                  bg="white"
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                />
              </Box>
              <Box w="100px">
                <Text fontSize="11px" fontWeight="600" color="gray.500" mb={1}>
                  Cor
                </Text>
                <Flex gap={1} flexWrap="wrap">
                  {COLOR_PRESETS.map(c => (
                    <Box
                      key={c}
                      w="18px"
                      h="18px"
                      borderRadius="full"
                      bg={c}
                      cursor="pointer"
                      border="2px solid"
                      borderColor={formCor === c ? 'gray.800' : 'transparent'}
                      onClick={() => setFormCor(c)}
                      transition="border-color 0.1s"
                      _hover={{ transform: 'scale(1.15)' }}
                    />
                  ))}
                </Flex>
              </Box>
            </Flex>
            <Box mt={2}>
              <Text fontSize="11px" fontWeight="600" color="gray.500" mb={1}>
                Icone
              </Text>
              <Flex gap={1} flexWrap="wrap">
                <Box
                  w="28px"
                  h="28px"
                  borderRadius="md"
                  bg="gray.100"
                  cursor="pointer"
                  border="2px solid"
                  borderColor={!formIcone ? 'gray.800' : 'transparent'}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  onClick={() => setFormIcone('')}
                  _hover={{ bg: 'gray.200' }}
                  transition="all 0.1s"
                  title="Sem icone"
                >
                  <Box w="8px" h="8px" borderRadius="full" bg={formCor} />
                </Box>
                {ICON_OPTIONS.map(({ key, Icon }) => (
                  <Box
                    key={key}
                    w="28px"
                    h="28px"
                    borderRadius="md"
                    bg={formIcone === key ? `${formCor}18` : 'gray.50'}
                    cursor="pointer"
                    border="2px solid"
                    borderColor={formIcone === key ? 'gray.800' : 'transparent'}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    onClick={() => setFormIcone(key)}
                    _hover={{ bg: `${formCor}18` }}
                    transition="all 0.1s"
                  >
                    <Icon size={14} color={formIcone === key ? formCor : '#A0AEC0'} weight="bold" />
                  </Box>
                ))}
              </Flex>
            </Box>
            <Flex gap={2} mt={3} justify="flex-end">
              <Button size="xs" variant="ghost" onClick={resetForm} borderRadius="full">
                Cancelar
              </Button>
              <Button
                size="xs"
                bg="gray.900"
                color="white"
                borderRadius="full"
                onClick={handleSubmit}
                isLoading={createMut.isPending || updateMut.isPending}
                _hover={{ bg: 'gray.800' }}
              >
                {editingId ? 'Salvar' : 'Criar'}
              </Button>
            </Flex>
          </Box>
        )}

        {!showForm && (
          <Button
            mt={3}
            size="sm"
            variant="ghost"
            color="gray.500"
            leftIcon={<Plus size={14} weight="bold" />}
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            borderRadius="full"
            fontWeight="500"
            fontSize="12px"
            _hover={{ bg: 'gray.50', color: 'gray.700' }}
          >
            Nova categoria
          </Button>
        )}
      </Box>

      <DialogModal
        isOpen={isDeleteOpen}
        status="warning"
        title="Excluir categoria?"
        description="Os servicos vinculados a esta categoria tambem serao afetados."
        onConfirm={confirmDelete}
        onCancel={onDeleteClose}
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
      />
    </>
  );
}

// ── Servicos Panel ──

function ServicosPanel({
  getCategorias,
  getServicos,
  createServico,
  updateServico,
  deleteServico,
  isOpen,
}: any) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formNome, setFormNome] = useState('');
  const [formCategoriaId, setFormCategoriaId] = useState<number | ''>('');
  const [filterCategoriaId, setFilterCategoriaId] = useState<number | ''>('');
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const { data: categoriasData } = useQuery<IDataNoPagination<IEventoCategoria[]>>({
    queryKey: ['evento-categorias'],
    queryFn: getCategorias,
    enabled: isOpen,
  });

  const { data: servicosData } = useQuery<IDataNoPagination<IEventoServico[]>>({
    queryKey: ['evento-servicos'],
    queryFn: getServicos,
    enabled: isOpen,
  });

  const categorias = categoriasData?.data ?? [];
  const allServicos = servicosData?.data ?? [];
  const servicos = filterCategoriaId
    ? allServicos.filter(s => s.categoria_id === filterCategoriaId)
    : allServicos;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['evento-servicos'] });

  const createMut = useMutation({
    mutationFn: (data: any) => createServico(data),
    onSuccess: () => {
      invalidate();
      toast.success('Servico criado!');
      resetForm();
    },
    onError: () => toast.error('Erro ao criar servico'),
  });

  const updateMut = useMutation({
    mutationFn: (data: any) => updateServico(data),
    onSuccess: () => {
      invalidate();
      toast.success('Servico atualizado!');
      resetForm();
    },
    onError: () => toast.error('Erro ao atualizar'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteServico(id),
    onSuccess: () => {
      invalidate();
      toast.success('Servico removido!');
    },
    onError: () => toast.error('Erro ao remover'),
  });

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormNome('');
    setFormCategoriaId('');
  };

  const handleEdit = (s: IEventoServico) => {
    setEditingId(s.id);
    setFormNome(s.nome);
    setFormCategoriaId(s.categoria_id);
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!formNome.trim()) {
      toast.error('Nome obrigatorio');
      return;
    }
    if (!formCategoriaId) {
      toast.error('Selecione uma categoria');
      return;
    }
    if (editingId) {
      updateMut.mutate({
        id: editingId,
        data: { nome: formNome.trim(), categoria_id: Number(formCategoriaId) },
      });
    } else {
      createMut.mutate({ nome: formNome.trim(), categoria_id: Number(formCategoriaId) });
    }
  };

  const confirmDelete = () => {
    if (deleteId) deleteMut.mutate(deleteId);
    setDeleteId(null);
    onDeleteClose();
  };

  // Agrupar por categoria
  const grouped: Record<string, { cor?: string; items: IEventoServico[] }> = {};
  servicos.forEach(s => {
    const key = s.categoria_nome || 'Sem categoria';
    if (!grouped[key]) grouped[key] = { cor: s.categoria_cor ?? undefined, items: [] };
    grouped[key].items.push(s);
  });

  return (
    <>
      <Box>
        {/* Filtro */}
        <Menu matchWidth>
          <MenuButton
            as={Button}
            size="sm"
            variant="outline"
            borderRadius="md"
            fontWeight="400"
            fontSize="13px"
            color={filterCategoriaId ? 'gray.700' : 'gray.400'}
            textAlign="left"
            w="100%"
            mb={4}
            rightIcon={<CaretDown size={12} />}
            _hover={{ borderColor: 'gray.300' }}
            _active={{ bg: 'white' }}
          >
            {filterCategoriaId
              ? categorias.find(c => c.id === filterCategoriaId)?.nome || 'Todas as categorias'
              : 'Todas as categorias'}
          </MenuButton>
          <MenuList
            borderRadius="12px"
            boxShadow="0 8px 24px rgba(0,0,0,0.12)"
            border="1px solid"
            borderColor="gray.100"
            py={1}
          >
            <MenuItem
              fontSize="13px"
              py={2}
              px={3}
              color="gray.400"
              _hover={{ bg: 'gray.50' }}
              onClick={() => setFilterCategoriaId('')}
            >
              Todas as categorias
            </MenuItem>
            {categorias.map(cat => (
              <MenuItem
                key={cat.id}
                fontSize="13px"
                py={2}
                px={3}
                _hover={{ bg: 'gray.50' }}
                onClick={() => setFilterCategoriaId(cat.id)}
                icon={<Box w="8px" h="8px" borderRadius="full" bg={cat.cor || 'gray.300'} />}
              >
                {cat.nome}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>

        {servicos.length === 0 && !showForm && (
          <Flex direction="column" align="center" py={8}>
            <Text fontSize="sm" color="gray.400">
              Nenhum servico cadastrado
            </Text>
          </Flex>
        )}

        {Object.entries(grouped).map(([catNome, { cor, items }]) => (
          <Box key={catNome} mb={4}>
            <Flex align="center" gap={2} mb={2}>
              {cor && <Box w="8px" h="8px" borderRadius="full" bg={cor} />}
              <Text
                fontSize="11px"
                fontWeight="700"
                color="gray.400"
                textTransform="uppercase"
                letterSpacing="0.05em"
              >
                {catNome}
              </Text>
              <Badge fontSize="9px" colorScheme="gray" borderRadius="full" px={1.5}>
                {items.length}
              </Badge>
            </Flex>
            {items.map(s => (
              <Flex
                key={s.id}
                align="center"
                gap={3}
                py={2}
                px={3}
                borderRadius="lg"
                _hover={{ bg: 'gray.50' }}
                transition="background 0.1s"
              >
                <Text fontSize="13px" fontWeight="500" color="gray.700" flex={1}>
                  {s.nome}
                </Text>
                <IconButton
                  aria-label="Editar"
                  icon={<PencilSimple size={14} />}
                  size="xs"
                  variant="ghost"
                  color="gray.400"
                  _hover={{ color: 'blue.500', bg: 'blue.50' }}
                  borderRadius="md"
                  onClick={() => handleEdit(s)}
                />
                <IconButton
                  aria-label="Excluir"
                  icon={<Trash size={14} />}
                  size="xs"
                  variant="ghost"
                  color="gray.400"
                  _hover={{ color: 'red.500', bg: 'red.50' }}
                  borderRadius="md"
                  onClick={() => {
                    setDeleteId(s.id);
                    onDeleteOpen();
                  }}
                />
              </Flex>
            ))}
          </Box>
        ))}

        {/* Form */}
        {showForm && (
          <Box
            mt={3}
            p={3}
            bg={editingId ? 'blue.50' : 'green.50'}
            borderRadius="lg"
            border="1px dashed"
            borderColor={editingId ? 'blue.200' : 'green.200'}
          >
            <Flex gap={2} align="end">
              <Box flex={1}>
                <Text fontSize="11px" fontWeight="600" color="gray.500" mb={1}>
                  Nome
                </Text>
                <Input
                  size="sm"
                  borderRadius="md"
                  value={formNome}
                  onChange={e => setFormNome(e.target.value)}
                  placeholder="Ex: Filmagem"
                  bg="white"
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                />
              </Box>
              <Box w="160px">
                <Text fontSize="11px" fontWeight="600" color="gray.500" mb={1}>
                  Categoria
                </Text>
                <Menu matchWidth>
                  <MenuButton
                    as={Button}
                    size="sm"
                    variant="outline"
                    borderRadius="md"
                    fontWeight="400"
                    fontSize="12px"
                    color={formCategoriaId ? 'gray.700' : 'gray.400'}
                    textAlign="left"
                    w="100%"
                    bg="white"
                    rightIcon={<CaretDown size={10} />}
                    _hover={{ borderColor: 'gray.300' }}
                    _active={{ bg: 'white' }}
                  >
                    {formCategoriaId
                      ? categorias.find(c => c.id === formCategoriaId)?.nome || 'Selecione...'
                      : 'Selecione...'}
                  </MenuButton>
                  <MenuList
                    borderRadius="12px"
                    boxShadow="0 8px 24px rgba(0,0,0,0.12)"
                    border="1px solid"
                    borderColor="gray.100"
                    py={1}
                    minW="160px"
                  >
                    {categorias.map(cat => (
                      <MenuItem
                        key={cat.id}
                        fontSize="12px"
                        py={2}
                        px={3}
                        _hover={{ bg: 'gray.50' }}
                        onClick={() => setFormCategoriaId(cat.id)}
                        icon={
                          <Box w="8px" h="8px" borderRadius="full" bg={cat.cor || 'gray.300'} />
                        }
                      >
                        {cat.nome}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </Box>
            </Flex>
            <Flex gap={2} mt={3} justify="flex-end">
              <Button size="xs" variant="ghost" onClick={resetForm} borderRadius="full">
                Cancelar
              </Button>
              <Button
                size="xs"
                bg="gray.900"
                color="white"
                borderRadius="full"
                onClick={handleSubmit}
                isLoading={createMut.isPending || updateMut.isPending}
                _hover={{ bg: 'gray.800' }}
              >
                {editingId ? 'Salvar' : 'Criar'}
              </Button>
            </Flex>
          </Box>
        )}

        {!showForm && (
          <Button
            mt={3}
            size="sm"
            variant="ghost"
            color="gray.500"
            leftIcon={<Plus size={14} weight="bold" />}
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            borderRadius="full"
            fontWeight="500"
            fontSize="12px"
            _hover={{ bg: 'gray.50', color: 'gray.700' }}
          >
            Novo servico
          </Button>
        )}
      </Box>

      <DialogModal
        isOpen={isDeleteOpen}
        status="warning"
        title="Excluir servico?"
        description="Esta acao nao pode ser desfeita."
        onConfirm={confirmDelete}
        onCancel={onDeleteClose}
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
      />
    </>
  );
}

// ── Checklist Templates Panel ──

const RESPONSAVEL_OPTIONS = [
  { value: 'juridico', label: 'Jurídico' },
  { value: 'cliente', label: 'Cliente' },
  { value: 'contabilidade', label: 'Contabilidade' },
  { value: 'producao', label: 'Produção' },
];

const RESPONSAVEL_COLORS: Record<string, string> = {
  juridico: 'purple',
  cliente: 'orange',
  contabilidade: 'cyan',
  producao: 'pink',
};

function ChecklistTemplatesPanel({
  getChecklistTemplateGrupos,
  createChecklistTemplateGrupo,
  updateChecklistTemplateGrupo,
  deleteChecklistTemplateGrupo,
  createChecklistTemplate: createItem,
  updateChecklistTemplate: updateItem,
  deleteChecklistTemplate: deleteItem,
  isOpen,
}: any) {
  const queryClient = useQueryClient();

  const [selectedGrupoId, setSelectedGrupoId] = useState<number | null>(null);

  // Grupo
  const [showAddGrupo, setShowAddGrupo] = useState(false);
  const [newGrupoNome, setNewGrupoNome] = useState('');
  const [editingGrupoId, setEditingGrupoId] = useState<number | null>(null);
  const [editGrupoNome, setEditGrupoNome] = useState('');

  // Item
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItemDesc, setNewItemDesc] = useState('');
  const [newItemResp, setNewItemResp] = useState('contabilidade');
  const [newItemObrig, setNewItemObrig] = useState(true);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editItemDesc, setEditItemDesc] = useState('');
  const [editItemResp, setEditItemResp] = useState('contabilidade');
  const [editItemObrig, setEditItemObrig] = useState(true);

  const [deleteTarget, setDeleteTarget] = useState<{ type: 'grupo' | 'item'; id: number } | null>(
    null,
  );
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const { data: gruposData } = useQuery<IDataNoPagination<IChecklistTemplateGrupo[]>>({
    queryKey: ['evento-checklist-template-grupos'],
    queryFn: getChecklistTemplateGrupos,
    enabled: isOpen,
  });

  const grupos = gruposData?.data ?? [];
  const selectedGrupo = grupos.find(g => g.id === selectedGrupoId);
  const items = selectedGrupo?.itens ?? [];

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['evento-checklist-template-grupos'] });

  // Grupo mutations
  const createGrupoMut = useMutation({
    mutationFn: (data: { nome: string }) => createChecklistTemplateGrupo(data),
    onSuccess: () => {
      invalidate();
      toast.success('Template criado');
      setNewGrupoNome('');
      setShowAddGrupo(false);
    },
    onError: () => toast.error('Erro ao criar template'),
  });

  const updateGrupoMut = useMutation({
    mutationFn: (data: { id: number; data: { nome: string } }) =>
      updateChecklistTemplateGrupo(data),
    onSuccess: () => {
      invalidate();
      toast.success('Template renomeado');
      setEditingGrupoId(null);
    },
    onError: () => toast.error('Erro ao renomear'),
  });

  const deleteGrupoMut = useMutation({
    mutationFn: (id: number) => deleteChecklistTemplateGrupo(id),
    onSuccess: () => {
      invalidate();
      toast.success('Template excluído');
      if (selectedGrupoId === deleteTarget?.id) setSelectedGrupoId(null);
    },
    onError: () => toast.error('Erro ao excluir'),
  });

  // Item mutations
  const createItemMut = useMutation({
    mutationFn: (data: any) => createItem(data),
    onSuccess: () => {
      invalidate();
      toast.success('Item adicionado');
      setNewItemDesc('');
      setNewItemResp('contabilidade');
      setNewItemObrig(true);
      setShowAddItem(false);
    },
    onError: () => toast.error('Erro ao adicionar item'),
  });

  const updateItemMut = useMutation({
    mutationFn: (data: any) => updateItem(data),
    onSuccess: () => {
      invalidate();
      toast.success('Item atualizado');
      setEditingItemId(null);
    },
    onError: () => toast.error('Erro ao atualizar'),
  });

  const deleteItemMut = useMutation({
    mutationFn: (id: number) => deleteItem(id),
    onSuccess: () => {
      invalidate();
      toast.success('Item removido');
    },
    onError: () => toast.error('Erro ao remover'),
  });

  const confirmDelete = () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'grupo') deleteGrupoMut.mutate(deleteTarget.id);
    else deleteItemMut.mutate(deleteTarget.id);
    setDeleteTarget(null);
    onDeleteClose();
  };

  // ── Sem grupo selecionado: lista de templates ──
  if (!selectedGrupoId) {
    return (
      <>
        <Box>
          {grupos.length === 0 && !showAddGrupo && (
            <Flex direction="column" align="center" py={8}>
              <Text fontSize="sm" color="gray.400">
                Nenhum template de checklist
              </Text>
              <Text fontSize="11px" color="gray.400" mt={1}>
                Crie templates para aplicar ao criar eventos
              </Text>
            </Flex>
          )}

          {grupos.map(grupo => {
            if (editingGrupoId === grupo.id) {
              return (
                <Flex key={grupo.id} gap={2} align="center" py={2} px={3}>
                  <Input
                    size="sm"
                    borderRadius="md"
                    value={editGrupoNome}
                    onChange={e => setEditGrupoNome(e.target.value)}
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter' && editGrupoNome.trim())
                        updateGrupoMut.mutate({
                          id: grupo.id,
                          data: { nome: editGrupoNome.trim() },
                        });
                      if (e.key === 'Escape') setEditingGrupoId(null);
                    }}
                  />
                  <IconButton
                    aria-label="Salvar"
                    icon={<Star size={14} />}
                    size="xs"
                    bg="blue.500"
                    color="white"
                    borderRadius="md"
                    onClick={() => {
                      if (editGrupoNome.trim())
                        updateGrupoMut.mutate({
                          id: grupo.id,
                          data: { nome: editGrupoNome.trim() },
                        });
                    }}
                    isLoading={updateGrupoMut.isPending}
                    _hover={{ bg: 'blue.600' }}
                  />
                </Flex>
              );
            }

            return (
              <Flex
                key={grupo.id}
                align="center"
                gap={3}
                py={2.5}
                px={3}
                borderRadius="lg"
                cursor="pointer"
                _hover={{ bg: 'gray.50' }}
                transition="background 0.1s"
                onClick={() => setSelectedGrupoId(grupo.id)}
              >
                <Box flex={1}>
                  <Text fontSize="13px" fontWeight="500" color="gray.700">
                    {grupo.nome}
                  </Text>
                  <Text fontSize="11px" color="gray.400">
                    {grupo.itens.length} {grupo.itens.length === 1 ? 'item' : 'itens'}
                  </Text>
                </Box>
                <IconButton
                  aria-label="Renomear"
                  icon={<PencilSimple size={14} />}
                  size="xs"
                  variant="ghost"
                  color="gray.400"
                  _hover={{ color: 'blue.500', bg: 'blue.50' }}
                  borderRadius="md"
                  onClick={e => {
                    e.stopPropagation();
                    setEditingGrupoId(grupo.id);
                    setEditGrupoNome(grupo.nome);
                  }}
                />
                <IconButton
                  aria-label="Excluir"
                  icon={<Trash size={14} />}
                  size="xs"
                  variant="ghost"
                  color="gray.400"
                  _hover={{ color: 'red.500', bg: 'red.50' }}
                  borderRadius="md"
                  onClick={e => {
                    e.stopPropagation();
                    setDeleteTarget({ type: 'grupo', id: grupo.id });
                    onDeleteOpen();
                  }}
                />
              </Flex>
            );
          })}

          {showAddGrupo ? (
            <Box
              mt={3}
              p={3}
              bg="green.50"
              borderRadius="lg"
              border="1px dashed"
              borderColor="green.200"
            >
              <Flex gap={2} align="end">
                <Box flex={1}>
                  <Text fontSize="11px" fontWeight="600" color="gray.500" mb={1}>
                    Nome do template
                  </Text>
                  <Input
                    size="sm"
                    borderRadius="md"
                    value={newGrupoNome}
                    onChange={e => setNewGrupoNome(e.target.value)}
                    placeholder="Ex: Show Musical"
                    bg="white"
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter' && newGrupoNome.trim())
                        createGrupoMut.mutate({ nome: newGrupoNome.trim() });
                      if (e.key === 'Escape') setShowAddGrupo(false);
                    }}
                  />
                </Box>
              </Flex>
              <Flex gap={2} mt={3} justify="flex-end">
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => setShowAddGrupo(false)}
                  borderRadius="full"
                >
                  Cancelar
                </Button>
                <Button
                  size="xs"
                  bg="gray.900"
                  color="white"
                  borderRadius="full"
                  onClick={() => {
                    if (newGrupoNome.trim()) createGrupoMut.mutate({ nome: newGrupoNome.trim() });
                  }}
                  isLoading={createGrupoMut.isPending}
                  isDisabled={!newGrupoNome.trim()}
                  _hover={{ bg: 'gray.800' }}
                >
                  Criar
                </Button>
              </Flex>
            </Box>
          ) : (
            <Button
              mt={3}
              size="sm"
              variant="ghost"
              color="gray.500"
              leftIcon={<Plus size={14} weight="bold" />}
              onClick={() => setShowAddGrupo(true)}
              borderRadius="full"
              fontWeight="500"
              fontSize="12px"
              _hover={{ bg: 'gray.50', color: 'gray.700' }}
            >
              Novo template
            </Button>
          )}
        </Box>

        <DialogModal
          isOpen={isDeleteOpen}
          status="warning"
          title="Excluir template?"
          description="Todos os itens deste template serao removidos."
          onConfirm={confirmDelete}
          onCancel={onDeleteClose}
          confirmLabel="Excluir"
          cancelLabel="Cancelar"
        />
      </>
    );
  }

  // ── Com grupo selecionado: itens do template ──
  return (
    <>
      <Box>
        <Flex align="center" gap={2} mb={4}>
          <Button
            size="xs"
            variant="ghost"
            borderRadius="full"
            onClick={() => {
              setSelectedGrupoId(null);
              setShowAddItem(false);
              setEditingItemId(null);
            }}
            color="gray.500"
            _hover={{ bg: 'gray.100' }}
          >
            ← Voltar
          </Button>
          <Text fontSize="14px" fontWeight="600" color="gray.700">
            {selectedGrupo?.nome}
          </Text>
          <Badge fontSize="9px" colorScheme="gray" borderRadius="full" px={1.5}>
            {items.length}
          </Badge>
        </Flex>

        {items.length === 0 && !showAddItem && (
          <Flex direction="column" align="center" py={8}>
            <Text fontSize="sm" color="gray.400">
              Nenhum item neste template
            </Text>
          </Flex>
        )}

        {items.map((item: IChecklistTemplate, idx: number) => {
          const respColor = RESPONSAVEL_COLORS[item.responsavel_tipo] || 'gray';

          if (editingItemId === item.id) {
            return (
              <Box
                key={item.id}
                p={3}
                mb={2}
                borderRadius="lg"
                border="1px solid"
                borderColor="blue.200"
                bg="blue.50"
              >
                <Input
                  size="sm"
                  borderRadius="md"
                  value={editItemDesc}
                  onChange={e => setEditItemDesc(e.target.value)}
                  autoFocus
                  mb={2}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && editItemDesc.trim())
                      updateItemMut.mutate({
                        id: item.id,
                        data: {
                          descricao: editItemDesc.trim(),
                          responsavel_tipo: editItemResp,
                          obrigatorio: editItemObrig,
                        },
                      });
                    if (e.key === 'Escape') setEditingItemId(null);
                  }}
                />
                <Flex gap={2} align="center">
                  <Menu matchWidth>
                    <MenuButton
                      as={Button}
                      size="xs"
                      variant="outline"
                      borderRadius="md"
                      fontWeight="400"
                      fontSize="12px"
                      rightIcon={<CaretDown size={10} />}
                      bg="white"
                    >
                      {RESPONSAVEL_OPTIONS.find(o => o.value === editItemResp)?.label}
                    </MenuButton>
                    <MenuList borderRadius="12px" py={1} minW="140px">
                      {RESPONSAVEL_OPTIONS.map(o => (
                        <MenuItem
                          key={o.value}
                          fontSize="12px"
                          py={2}
                          px={3}
                          onClick={() => setEditItemResp(o.value)}
                        >
                          {o.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                  <Button
                    size="xs"
                    variant={editItemObrig ? 'solid' : 'outline'}
                    colorScheme="red"
                    borderRadius="md"
                    fontSize="10px"
                    onClick={() => setEditItemObrig(!editItemObrig)}
                  >
                    OBR
                  </Button>
                  <Box flex={1} />
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() => setEditingItemId(null)}
                    borderRadius="full"
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="xs"
                    bg="gray.900"
                    color="white"
                    borderRadius="full"
                    onClick={() => {
                      if (editItemDesc.trim())
                        updateItemMut.mutate({
                          id: item.id,
                          data: {
                            descricao: editItemDesc.trim(),
                            responsavel_tipo: editItemResp,
                            obrigatorio: editItemObrig,
                          },
                        });
                    }}
                    isLoading={updateItemMut.isPending}
                    _hover={{ bg: 'gray.800' }}
                  >
                    Salvar
                  </Button>
                </Flex>
              </Box>
            );
          }

          return (
            <Flex
              key={item.id}
              align="center"
              gap={3}
              py={2}
              px={3}
              borderRadius="lg"
              _hover={{ bg: 'gray.50' }}
              transition="background 0.1s"
            >
              <Text fontSize="11px" fontWeight="600" color="gray.300" w="18px" textAlign="center">
                {idx + 1}
              </Text>
              <Box flex={1} minW={0}>
                <Text fontSize="13px" fontWeight="500" color="gray.700" noOfLines={1}>
                  {item.descricao}
                </Text>
                <Flex gap={1} mt={0.5}>
                  <Badge fontSize="2xs" colorScheme={respColor} variant="outline">
                    {RESPONSAVEL_OPTIONS.find(o => o.value === item.responsavel_tipo)?.label}
                  </Badge>
                  {item.obrigatorio && (
                    <Badge fontSize="2xs" colorScheme="red" variant="subtle">
                      OBR
                    </Badge>
                  )}
                </Flex>
              </Box>
              <IconButton
                aria-label="Editar"
                icon={<PencilSimple size={14} />}
                size="xs"
                variant="ghost"
                color="gray.400"
                _hover={{ color: 'blue.500', bg: 'blue.50' }}
                borderRadius="md"
                onClick={() => {
                  setEditingItemId(item.id);
                  setEditItemDesc(item.descricao);
                  setEditItemResp(item.responsavel_tipo);
                  setEditItemObrig(item.obrigatorio);
                }}
              />
              <IconButton
                aria-label="Excluir"
                icon={<Trash size={14} />}
                size="xs"
                variant="ghost"
                color="gray.400"
                _hover={{ color: 'red.500', bg: 'red.50' }}
                borderRadius="md"
                onClick={() => {
                  setDeleteTarget({ type: 'item', id: item.id });
                  onDeleteOpen();
                }}
              />
            </Flex>
          );
        })}

        {showAddItem ? (
          <Box
            mt={3}
            p={3}
            bg="green.50"
            borderRadius="lg"
            border="1px dashed"
            borderColor="green.200"
          >
            <Input
              size="sm"
              borderRadius="md"
              placeholder="Descrição do item"
              value={newItemDesc}
              onChange={e => setNewItemDesc(e.target.value)}
              bg="white"
              autoFocus
              mb={2}
              onKeyDown={e => {
                if (e.key === 'Enter' && newItemDesc.trim())
                  createItemMut.mutate({
                    grupo_id: selectedGrupoId,
                    descricao: newItemDesc.trim(),
                    responsavel_tipo: newItemResp,
                    obrigatorio: newItemObrig,
                    ordem: items.length + 1,
                  });
                if (e.key === 'Escape') setShowAddItem(false);
              }}
            />
            <Flex gap={2} align="center">
              <Menu matchWidth>
                <MenuButton
                  as={Button}
                  size="xs"
                  variant="outline"
                  borderRadius="md"
                  fontWeight="400"
                  fontSize="12px"
                  rightIcon={<CaretDown size={10} />}
                  bg="white"
                >
                  {RESPONSAVEL_OPTIONS.find(o => o.value === newItemResp)?.label}
                </MenuButton>
                <MenuList borderRadius="12px" py={1} minW="140px">
                  {RESPONSAVEL_OPTIONS.map(o => (
                    <MenuItem
                      key={o.value}
                      fontSize="12px"
                      py={2}
                      px={3}
                      onClick={() => setNewItemResp(o.value)}
                    >
                      {o.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
              <Button
                size="xs"
                variant={newItemObrig ? 'solid' : 'outline'}
                colorScheme="red"
                borderRadius="md"
                fontSize="10px"
                onClick={() => setNewItemObrig(!newItemObrig)}
              >
                OBR
              </Button>
              <Box flex={1} />
              <Button
                size="xs"
                variant="ghost"
                onClick={() => setShowAddItem(false)}
                borderRadius="full"
              >
                Cancelar
              </Button>
              <Button
                size="xs"
                bg="gray.900"
                color="white"
                borderRadius="full"
                onClick={() => {
                  if (newItemDesc.trim())
                    createItemMut.mutate({
                      grupo_id: selectedGrupoId,
                      descricao: newItemDesc.trim(),
                      responsavel_tipo: newItemResp,
                      obrigatorio: newItemObrig,
                      ordem: items.length + 1,
                    });
                }}
                isLoading={createItemMut.isPending}
                isDisabled={!newItemDesc.trim()}
                _hover={{ bg: 'gray.800' }}
              >
                Adicionar
              </Button>
            </Flex>
          </Box>
        ) : (
          <Button
            mt={3}
            size="sm"
            variant="ghost"
            color="gray.500"
            leftIcon={<Plus size={14} weight="bold" />}
            onClick={() => setShowAddItem(true)}
            borderRadius="full"
            fontWeight="500"
            fontSize="12px"
            _hover={{ bg: 'gray.50', color: 'gray.700' }}
          >
            Novo item
          </Button>
        )}
      </Box>

      <DialogModal
        isOpen={isDeleteOpen}
        status="warning"
        title={deleteTarget?.type === 'grupo' ? 'Excluir template?' : 'Excluir item?'}
        description={
          deleteTarget?.type === 'grupo'
            ? 'Todos os itens deste template serao removidos.'
            : 'Esta acao nao pode ser desfeita.'
        }
        onConfirm={confirmDelete}
        onCancel={onDeleteClose}
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
      />
    </>
  );
}
